import { ACCOUNT_SETTING_DEFAULTS, TAccountSettings } from "@shared/types";

/** An account's effective settings: its stored values over the defaults. */
export const resolveAccountSettings = (
  stored?: Partial<TAccountSettings> | null,
): TAccountSettings => ({
  ...ACCOUNT_SETTING_DEFAULTS,
  ...stored,
});

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * When an archived project's documents (those not marked keep) are due to be deleted: its archive
 * date plus the account's retention period. Null when it isn't archived or the account keeps
 * documents forever. Always derived from the current setting, so changing it moves every date.
 */
export const getDocumentsDeleteOn = (
  archivedAt: Date | string | null | undefined,
  settings: TAccountSettings,
): Date | null => {
  if (!archivedAt || settings.documentRetentionDays === null) return null;
  return new Date(
    new Date(archivedAt).getTime() +
      settings.documentRetentionDays * MS_PER_DAY,
  );
};

/** Members get at least this much notice, from the first warning email, before documents are deleted */
export const DOCUMENT_DELETION_NOTICE_DAYS = 14;

type TArchivedProjectDates = {
  archived_at?: Date | string | null;
  documents_warned_at?: Date | string | null;
};

/**
 * When an archived project's documents will actually be deleted: their retention date, but never
 * less than DOCUMENT_DELETION_NOTICE_DAYS after the first warning (or from now, if it hasn't gone
 * out yet), so shortening the retention period can't cut the notice short. Null when the project
 * isn't archived or the account keeps documents forever.
 */
export const getDocumentsDeletionDate = (
  project: TArchivedProjectDates,
  settings: TAccountSettings,
  now: Date = new Date(),
): Date | null => {
  const retentionDate = getDocumentsDeleteOn(project.archived_at, settings);
  if (!retentionDate) return null;
  const noticeFrom = project.documents_warned_at
    ? new Date(project.documents_warned_at)
    : now;
  const noticeEnds =
    noticeFrom.getTime() + DOCUMENT_DELETION_NOTICE_DAYS * MS_PER_DAY;
  return new Date(Math.max(retentionDate.getTime(), noticeEnds));
};
